import "./FeaturesList.scss";

export default {
  props: ["editedItem", "pFeaturesGroups", "isOwnedBySubPrincipal"],
  data() {
    return {
      searchQuery: "",
      expandedGroups: [],
      maxVisibleChips: 5,
      trashHover: false
    };
  },
  async mounted() {
    try {
      if (!this.pFeaturesGroups) {
        await this.$store.dispatch("getFeatureGroupsForPois", this.isRegion);
      }
      await this.$store.dispatch("getCategories");
      await this.$store.dispatch("getGlobalConfig");
    } catch (error) {
      await this.$store.dispatch("createError", error);
    }
  },
  computed: {
    currentPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    isPoiAdmin() {
      return (
        this.isAdmin || this.currentUser?.scopes?.includes("role:poi_admin")
      );
    },
    isRegionEdit() {
      return (
        this.isAdmin ||
        this.isRegion ||
        this.currentUser?.scopes?.includes("role:region_edit")
      );
    },
    isRegionsAdmin() {
      return (
        this.isAdmin ||
        this.isRegion ||
        this.currentUser?.scopes?.includes("role:regions_admin")
      );
    },
    config() {
      return this.$store.state.globalConfig;
    },
    favoriteFeatures() {
      const favoriteFeatureIds = this.config.favoriteFeatures ?? [];
      return favoriteFeatureIds
        .map(id => this.allFeatures.find(feature => feature._id === id))
        .filter(Boolean);
    },
    categories() {
      return this.$store.state.categories;
    },
    categoryGroups() {
      return this.$store.state.categoryGroups;
    },
    featureGroups() {
      const poiCategories = [this.editedItem.category].concat(
        this.editedItem?.additionalCategories ?? []
      );
      const categoryGroups = poiCategories
        .map(
          catId => this.categories.find(cat => cat._id === catId)?.categoryGroup
        )
        .filter(catGroup => !!catGroup);

      const categoryTypes = [];

      categoryGroups.forEach(group => {
        const type = this.categoryGroups.find(_group => _group._id === group)
          ?.categoryType;
        if (type) {
          categoryTypes.push(type);
        }
      });

      const featureGroups = (
        this.pFeaturesGroups || this.$store.state.featureGroups
      )?.filter(
        featureGroup =>
          featureGroup.categoryTypes?.some(ct => categoryTypes.includes(ct)) ||
          !featureGroup.categoryTypes?.length
      );
      return featureGroups.filter(fg => {
        return (
          (((fg.categoryTypes?.includes(this.editedItem.categoryType) ||
            !this.editedItem.categoryType) &&
            fg.features.length &&
            fg.categoryGroups.find(cg => categoryGroups.includes(cg._id))) ||
            !fg.categoryGroups.length) &&
          !fg.services?.length
        );
      });
    },
    filteredFeatureGroups() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.featureGroups;
      }
      const searchTerms = this.searchQuery
        .trim()
        .toLowerCase()
        .split(",")
        .map(str => str.trim())
        .filter(Boolean);

      return this.featureGroups
        .map(group => {
          const filteredFeatures = group.features.filter(feature =>
            searchTerms.some(term => feature.title.toLowerCase().includes(term))
          );
          if (filteredFeatures.length) {
            return { ...group, features: filteredFeatures };
          }
          if (
            searchTerms.some(term => group.title.toLowerCase().includes(term))
          ) {
            return { ...group, features: [] };
          }
        })
        .filter(Boolean);
    },
    ownFeatureGroups() {
      return (
        this.featureGroups?.filter(
          featureGroup => featureGroup.owner === this.currentPrincipal?.id
        ) ?? []
      );
    },
    ownFeatures() {
      return this.ownFeatureGroups
        .map(featureGroup => featureGroup.features)
        .flat()
        .map(feature => feature._id);
    },
    isRegion() {
      return (
        this.$store.state.currentUser?.principal_id === this.REGION_PRINCIPAL
      );
    },
    allFeatures() {
      return this.featureGroups.map(fg => fg.features).flat();
    }
  },
  methods: {
    isFeatureSelected(feature) {
      return this.editedItem.features.includes(feature._id);
    },
    toggleFeature(feature) {
      if (this.isFeatureDisabled(feature)) return;
      const isSelected = this.isFeatureSelected(feature);
      this.featureChecked(!isSelected, feature);
    },
    removeFeature(feature) {
      if (this.isFeatureDisabled(feature)) return;
      this.featureChecked(false, feature);
    },
    featureChecked(checked, feature) {
      if (this.isFeatureDisabled(feature)) {
        return;
      }
      const id = feature._id;
      if (checked && !this.editedItem.features.includes(id)) {
        this.editedItem.features.push(id);
      } else if (!checked && this.editedItem.features.includes(id)) {
        this.editedItem.features.remove(id);
      }
      this.editedItem.features = [...new Set(this.editedItem.features)];
    },
    getCheckboxFeatures(featureGroup) {
      return featureGroup.features;
    },
    getSelectedCount(featureGroup) {
      return featureGroup.features.filter(f =>
        this.editedItem.features.includes(f._id)
      ).length;
    },
    getVisibleFeatures(featureGroup) {
      if (this.isExpanded(featureGroup._id) || this.searchQuery) {
        return featureGroup.features;
      }
      return featureGroup.features.slice(0, this.maxVisibleChips);
    },
    hasMoreFeatures(featureGroup) {
      return (
        featureGroup.features.length > this.maxVisibleChips && !this.searchQuery
      );
    },
    getHiddenCount(featureGroup) {
      return featureGroup.features.length - this.maxVisibleChips;
    },
    isExpanded(groupId) {
      return this.expandedGroups.includes(groupId);
    },
    toggleExpand(groupId) {
      if (this.isExpanded(groupId)) {
        this.expandedGroups = this.expandedGroups.filter(id => id !== groupId);
      } else {
        this.expandedGroups.push(groupId);
      }
    },
    isOwnFeatureGroup(featureGroup) {
      return this.ownFeatureGroups.some(
        _featureGroup => _featureGroup._id === featureGroup
      );
    },
    isFeatureDisabled(feature) {
      if (!this.isFeatureEditable(feature)) {
        return true;
      }
      if (!this.isOwnedBySubPrincipal) {
        return false;
      }
      return !this.isOwnFeature(feature._id);
    },
    isOwnFeature(featureId) {
      return this.ownFeatures.includes(featureId);
    },
    isFeatureEditable(feature) {
      if (!feature?.editable_by || feature.editable_by === "all") {
        return true;
      }
      if (feature.editable_by === "poi_admin") {
        return this.isPoiAdmin;
      }
      if (feature.editable_by === "admin") {
        return this.isAdmin;
      }
      if (feature.editable_by === "region_edit") {
        return this.isRegionEdit || this.isRegionsAdmin || this.isPoiAdmin;
      }
      if (feature.editable_by === "regions_admin") {
        return this.isRegionsAdmin || this.isPoiAdmin;
      }
      return true;
    }
  }
};
